import React, {
  createContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  getFirestore,
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
} from "firebase/firestore";
import { startCollectingTodayTimeRecordsByProjectUseCase } from "../useCases/startCollectingTodayTimeRecordsByProject";
import { generateNumericId, toDocId, toRefValue } from "../utils/ids";
import { buildConvertedTask } from "../utils/buildConvertedTask";
import { buildAction } from "../utils/buildAction";
import { nextActionOrder } from "../utils/actionsOrdering";
import { ALL_PROJECTS, isAllProjects } from "../constants/projects";
import { useCommonUI } from "./CommonUIContext";
import { docToObject } from "../utils/docToObject";
import { getDeviceId } from "../utils/deviceId";
import { isMultiTrackingEnabled } from "../utils/multiTrackingSetting";
import { buildLabel } from "../utils/buildLabel";
import {
  nextFireAfter,
  REMOTE_STATE_DELETE,
  REMOTE_STATE_SYNCED,
  TASK_STATE_BACKLOG,
} from "../utils/recurrence";
import { materializeDueRecurringRules as materializeDueRecurringRulesUseCase } from "../domain/recurring/materializeRecurringTasks";
import { sweepDueDateNotifications } from "../domain/notifications/sweepDueDateNotifications";

export const FirebaseContext = createContext();

export const FirebaseProvider = ({ children }) => {
  const [firebaseApp, setFirebaseApp] = useState(null);
  const [firebaseAuth, setFirebaseAuth] = useState(null);
  const [firebaseAnalytics, setFirebaseAnalytics] = useState(null);
  const [firebaseFirestore, setFirebaseFirestore] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [activeProject, setActiveProject] = useState(null);
  const [lastRecordedTask, setLastRecordedTask] = useState(null);
  const [recordingTaskId, setRecordingTaskId] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  // ALL of the user's time records (unfiltered). Time records are scoped to a
  // project via their task, NOT a projectId field — Android's TimeRecordData has
  // no projectId, so the vast majority of records (everything tracked on mobile)
  // carry none. `projectTimeRecords` is derived below by taskId membership.
  const [allTimeRecords, setAllTimeRecords] = useState([]);
  // Time records for the active project (or all projects), derived by taskId
  // membership against the loaded `tasks`. In All Projects mode `tasks` already
  // holds every task, so this naturally includes every record — matching the
  // total Android shows. Declared here (with the other hooks, before any early
  // return) so the hook order stays stable.
  const projectTimeRecords = useMemo(() => {
    const ids = new Set(tasks.map((t) => Number(t.id)));
    return allTimeRecords.filter((r) => ids.has(Number(r.taskId)));
  }, [allTimeRecords, tasks]);
  const [todayTimeRecords, setTodayTimeRecords] = useState([]);
  const [todayTimeRecordsByProject, setTodayTimeRecordsByProject] = useState(
    []
  );
  const [wipTasks, setWipTasks] = useState([]);
  const wipTasksUnsubscribeRef = useRef(null);
  const [taskDetail, setTaskDetail] = useState(null);
  const [trackedTasks, setTrackedTasks] = useState([]);
  const trackedTasksUnsubscribeRef = useRef(null);
  const [labels, setLabels] = useState([]);
  const labelsUnsubscribeRef = useRef(null);
  const [actionTemplates, setActionTemplates] = useState([]);
  const actionTemplatesUnsubscribeRef = useRef(null);
  const [recurringRules, setRecurringRules] = useState([]);
  const recurringRulesUnsubscribeRef = useRef(null);
  const [notifications, setNotifications] = useState([]);
  const notificationsUnsubscribeRef = useRef(null);
  // Invoices + clients — synced Firestore collections written in Android's
  // InvoiceData / ClientData wire format (same field names + numeric Int32 ids)
  // so they line up when Android promotes these to synced entities.
  const [invoices, setInvoices] = useState([]);
  const invoicesUnsubscribeRef = useRef(null);
  const [clients, setClients] = useState([]);
  const clientsUnsubscribeRef = useRef(null);
  const materializingRef = useRef(false);
  // Live mirrors for the user-doc listener, which is subscribed once and must
  // not resubscribe on every selection change (each resubscription re-emits
  // the cached, possibly pre-write doc — the race that used to bounce the
  // selection back and desync the task list from the chip).
  const activeProjectRef = useRef(null);
  const projectsRef = useRef([]);
  activeProjectRef.current = activeProject;
  projectsRef.current = projects;

  const tasksUnsubscribeRef = useRef(null);
  const timeRecordsUnsubscribeRef = useRef(null);
  const todayTimeRecordsUnsubscribeRef = useRef(null);
  const todayTimeRecordsByProjectUnsubscribeRef = useRef(null);
  const taskDetailUnsubscribeRef = useRef(null);

  const { showSnackbar } = useCommonUI();

  useEffect(() => {
    // Two Firebase projects, mirroring Android's flavors: dev
    // (anothertaskdev-66e4b) is the default; prod (anothertask-3c446) is
    // selected by building with REACT_APP_FIREBASE_ENV=prod (npm run
    // build:prod) and deploying with --project prod. Client config values are
    // not secrets — this matches Firebase's own hosting init output.
    const DEV_CONFIG = {
      apiKey: "AIzaSyC3RPlsXRSh8XAo9D3HEDAdttWCHxds4Jc",
      authDomain: "anothertaskdev-66e4b.firebaseapp.com",
      projectId: "anothertaskdev-66e4b",
      storageBucket: "anothertaskdev-66e4b.appspot.com",
      messagingSenderId: "494795849308",
      appId: "1:494795849308:web:4e47ee6e4d13e674a9b099",
      measurementId: "G-QDXKNTDELD",
    };
    const PROD_CONFIG = {
      apiKey: "AIzaSyDFEd-qcTP3O1T0UOroCiyJrhFb2-5QGV8",
      authDomain: "anothertask-3c446.firebaseapp.com",
      projectId: "anothertask-3c446",
      storageBucket: "anothertask-3c446.firebasestorage.app",
      messagingSenderId: "331815339272",
      appId: "1:331815339272:web:eea91778f453a295e6d8bd",
      measurementId: "G-RDNML1EFZ9",
    };
    const firebaseConfig =
      process.env.REACT_APP_FIREBASE_ENV === "prod" ? PROD_CONFIG : DEV_CONFIG;

    const app = initializeApp(firebaseConfig);
    const analytics = getAnalytics(app);
    const auth = getAuth(app);
    const firestore = getFirestore(app);

    setFirebaseApp(app);
    setFirebaseAnalytics(analytics);
    setFirebaseAuth(auth);
    setFirebaseFirestore(firestore);


    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setAuthLoading(false);

      if (user) {
        setDataLoading(true);
        fetchProjects(firestore, user);
      }
    });

    return () => unsubscribe();
  }, []);

  // Fetch the recording task data locally when the recordingTaskId or tasks changes
  useEffect(() => {
    if (recordingTaskId && tasks.length > 0) {
      const foundRecordingTask = tasks.find(
        (task) => task.id === recordingTaskId
      );

      if (foundRecordingTask) {
        setLastRecordedTask(foundRecordingTask);
      } else {
        setLastRecordedTask(null);
      }
    }
  }, [recordingTaskId, tasks]);

  // The user-doc listener only maps active_project_id -> activeProject state
  // (initial load + genuinely remote changes, e.g. switching on Android). It
  // never fetches tasks itself — the tasks subscription follows activeProject
  // in its own effect below — and it reads current state through refs so it is
  // subscribed once per session instead of resubscribing on every selection
  // change. The old version resubscribed on [projects, activeProject]: each
  // resubscription immediately re-emitted the cached doc, which still held the
  // PREVIOUS project while updateSelectedProjectInFirestore's write was in
  // flight, bouncing the selection back; the write's echo was then swallowed
  // by a stale-closure guard, leaving the chip on "All Projects" but the task
  // listener filtered to the old project until a manual refresh.
  const hasProjects = projects.length > 0;
  useEffect(() => {
    if (!currentUser || !firebaseFirestore || !hasProjects) return;
    const userDocRef = doc(firebaseFirestore, `users/${currentUser.uid}`);
    const unsubscribeUser = onSnapshot(userDocRef, async (doc) => {
      const current = activeProjectRef.current;
      const knownProjects = projectsRef.current;
      if (doc.exists()) {
        const selectedProjectId = doc.data().active_project_id;
        const recordingTaskId = doc.data().last_recorded_task_id;
        if (!selectedProjectId && knownProjects.length > 0) {
          const firstProject = knownProjects[0];
          await updateSelectedProjectInFirestore(firstProject.id);
          setActiveProject(firstProject);
        } else if (isAllProjects(selectedProjectId)) {
          if (!current || !isAllProjects(current.id)) {
            setActiveProject(ALL_PROJECTS);
          }
        } else if (selectedProjectId) {
          // String-compare: web stores string doc ids, Android writes the
          // numeric id — they must still match.
          const selectedProject = knownProjects.find(
            (project) => String(project.id) === String(selectedProjectId)
          );
          if (
            selectedProject &&
            String(selectedProjectId) !== String(current?.id)
          ) {
            setActiveProject(selectedProject);
          }
        }
        if (recordingTaskId) {
          setRecordingTaskId(recordingTaskId);
        }
      } else {
        // If document doesn't exist, set the first project as the active project
        if (knownProjects.length > 0) {
          const firstProject = knownProjects[0];
          await setDoc(userDocRef, { active_project_id: firstProject.id });
          setActiveProject(firstProject);
        }
      }
    });

    return () => unsubscribeUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, firebaseFirestore, hasProjects]);

  // Tasks always follow the active project: one subscription driven by the
  // selection, so the chip and the task list can never point at different
  // projects. Every selection writer (dropdown, user-doc listener, project
  // creation) just sets activeProject and this effect re-subscribes.
  useEffect(() => {
    if (!currentUser || !firebaseFirestore || !activeProject) return;
    fetchTasks(firebaseFirestore, currentUser, activeProject.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, firebaseFirestore, activeProject?.id]);

  // Keep the selected project's data fresh. `activeProject` holds a snapshot of
  // the project object, so editing it elsewhere (e.g. renaming on the project
  // detail screen) would otherwise leave the chip showing the stale name. When
  // the live `projects` list changes, re-derive the active project from it. The
  // field guard makes this a no-op when nothing changed, so it can't loop.
  useEffect(() => {
    if (!activeProject || isAllProjects(activeProject.id)) return;
    const fresh = projects.find((p) => p.id === activeProject.id);
    if (
      fresh &&
      (fresh.projectName !== activeProject.projectName ||
        fresh.projectDescription !== activeProject.projectDescription ||
        fresh.projectArchived !== activeProject.projectArchived ||
        fresh.feedData !== activeProject.feedData)
    ) {
      setActiveProject(fresh);
    }
  }, [projects, activeProject]);

  useEffect(() => {
    return () => {
      if (timeRecordsUnsubscribeRef.current) {
        timeRecordsUnsubscribeRef.current();
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (todayTimeRecordsUnsubscribeRef.current) {
        todayTimeRecordsUnsubscribeRef.current();
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (wipTasksUnsubscribeRef.current) {
        wipTasksUnsubscribeRef.current();
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (trackedTasksUnsubscribeRef.current) {
        trackedTasksUnsubscribeRef.current();
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (labelsUnsubscribeRef.current) {
        labelsUnsubscribeRef.current();
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (taskDetailUnsubscribeRef.current) {
        taskDetailUnsubscribeRef.current();
      }
    };
  }, []);

  const fetchProjects = (firestore, user) => {
    if (!user) return;

    const userProjectsRef = collection(firestore, `users/${user.uid}/projects`);


    const unsubscribeProjects = onSnapshot(
      userProjectsRef,
      async (snapshot) => {
        const projectList = snapshot.docs
          .map((doc) => docToObject(doc))
          .filter((project) => !project.projectArchived);


        if (projectList.length === 0) {
          // If no projects found, create a default project
          await createNewProject();
        }
        setProjects(projectList);
        setDataLoading(false);
      }
    );

    return unsubscribeProjects;
  };

  const createNewProject = async () => {
    if (!currentUser || !firebaseFirestore) return;
    try {
      // Numeric, Int32-safe id so the Android (Room) app can ingest this
      // project. Doc path id, the `projectId` field and `firestoreId` all
      // mirror the wire format Android writes. See utils/ids.js.
      const newId = generateNumericId(projects.map((p) => p.id));
      const docId = toDocId(newId);

      const defaultProject = {
        projectId: newId,
        projectName: "New Project",
        projectDescription: "This is a new project.",
        projectArchived: false,
        feedData: true,
        remoteState: "remote_state_synced",
        firestoreId: docId,
        updatedAt: Date.now(),
      };

      // Write at a chosen (numeric) doc id rather than letting Firestore
      // auto-generate a random string id.
      await setDoc(
        doc(firebaseFirestore, `users/${currentUser.uid}/projects`, docId),
        defaultProject
      );


      // Set the default project as active and persist the selection (so the
      // user-doc listener doesn't later snap back to the previous project).
      // The tasks subscription follows activeProject automatically.
      setActiveProject({ id: docId, ...defaultProject });
      await updateSelectedProjectInFirestore(docId);
      // Return the new doc id so callers can open its detail screen for editing.
      return docId;
    } catch (error) {
      console.error("Error creating default project:", error);
    }
  };

  const createNewTask = async (useAsNote) => {
    try {
      // In All Projects mode there is no concrete target project, so fall
      // back to the first available project (mirrors Android landing a new
      // task on a real project rather than the -1 sentinel).
      const targetProject =
        activeProject && !isAllProjects(activeProject.id)
          ? activeProject
          : projects[0];
      if (!targetProject) {
        console.error("No project available to create the task in.");
        return;
      }

      // Numeric, Int32-safe id + Android (Room) TaskData wire format. `id`
      // and `projectId` are written as numbers; doc path id is the numeric
      // string. See utils/ids.js.
      const newId = generateNumericId(tasks.map((t) => t.id));
      const docId = toDocId(newId);

      const newTask = {
        id: newId,
        title: "",
        description: "",
        projectId: toRefValue(targetProject.id),
        usedAsNote: useAsNote,
        feedData: true,
        labelIds: [],
        remoteState: "remote_state_synced",
        firestoreId: docId,
        updatedAt: Date.now(),
        ...(useAsNote
          ? {} // Skip these fields if it's a note
          : {
              // Android TaskData.dueDate is a non-null Long (0 = "no due
              // date"); never write null or doc.toObject() mismaps it.
              dueDate: 0,
              taskState: "task_state_backlog",
              estimatedTime: 0,
              // Per-task AI tracking toggle ("Track AI on this task"), default on
              // (Android TaskData.aiTrackingEnabled = true).
              aiTrackingEnabled: true,
            }),
      };

      await setDoc(
        doc(firebaseFirestore, `users/${currentUser.uid}/tasks`, docId),
        newTask
      );

      return docId;
    } catch (error) {
      console.error("Error creating task:", error);
      throw error;
    }
  };

  const fetchTasks = (firestore, user, projectId) => {
    if (!user) return;


    // Unsubscribe the previous listener if it exists
    if (tasksUnsubscribeRef.current) {
      tasksUnsubscribeRef.current();
      tasksUnsubscribeRef.current = null;
    }

    const userTasksRef = collection(firestore, `users/${user.uid}/tasks`);
    // All Projects mode: every task across projects (no projectId filter),
    // mirroring Android's getAllTasksByFilter fallback.
    const tasksQuery = isAllProjects(projectId)
      ? userTasksRef
      : query(
          userTasksRef,
          where(
            "projectId",
            "==",
            isNaN(projectId) ? projectId : Number(projectId)
          )
        );


    const unsubscribeTasks = onSnapshot(tasksQuery, (snapshot) => {
      const taskList = snapshot.docs.map((doc) => docToObject(doc));
      setTasks(taskList);
    });

    // Store the unsubscribe function in the ref
    tasksUnsubscribeRef.current = unsubscribeTasks;
  };

  const updateSelectedProjectInFirestore = async (projectId) => {
    if (!currentUser || !firebaseFirestore) return;

    try {
      const userDocRef = doc(firebaseFirestore, `users/${currentUser.uid}`);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        await updateDoc(userDocRef, {
          active_project_id: projectId,
        });
      } else {
        // If the document doesn't exist, create it with the active_project_id
        await setDoc(userDocRef, {
          active_project_id: projectId,
        });
      }
    } catch (error) {
      console.error("Error updating active project:", error);
    }
  };

  const updateRecordingTaskInFirestore = async (taskId) => {
    if (!currentUser || !firebaseFirestore) return;
    try {
      const userDocRef = doc(firebaseFirestore, `users/${currentUser.uid}`);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        await updateDoc(userDocRef, { last_recorded_task_id: taskId });
      } else {
        await setDoc(userDocRef, { last_recorded_task_id: taskId });
      }
    } catch (error) {
      console.error("Error updating recording task:", error);
    }
  };

  const handleProjectChange = (newProjectId) => {
    if (!currentUser) return;

    const selectedProject = isAllProjects(newProjectId)
      ? ALL_PROJECTS
      : projects.find(
          (project) => String(project.id) === String(newProjectId)
        );
    if (!selectedProject) return;
    // Local state is the source of truth: the tasks subscription follows
    // activeProject. The Firestore write only persists the selection for
    // reload / other devices.
    setActiveProject(selectedProject);
    updateSelectedProjectInFirestore(newProjectId);
  };

  const updateProject = async (projectId, updatedFields) => {
    if (!currentUser || !firebaseFirestore) return;

    try {
      const projectRef = doc(
        firebaseFirestore,
        `users/${currentUser.uid}/projects`,
        projectId
      );
      // Bump updatedAt so Android's last-write-wins sync ingests the edit.
      await updateDoc(projectRef, { ...updatedFields, updatedAt: Date.now() });
    } catch (error) {
      console.error("Error updating project:", error);
    }
  };

  // Cascade-delete a project and everything that hangs off it, mirroring
  // Android's MarkProjectAsDelete (whose net Firestore effect is a hard delete
  // of the same set of docs). Scope:
  //   * tasks (incl. notes — TaskData with usedAsNote) by projectId
  //   * time records by projectId
  //   * actions whose taskId belongs to one of those tasks (actions carry no
  //     projectId, so we scope by the project's task ids)
  //   * labels scoped to this project (globals — projectId null — are kept)
  // The project doc itself is removed last. Android's foreground snapshot
  // listeners ingest the doc removals and delete the matching Room rows.
  const deleteProject = async (projectId) => {
    if (!currentUser || !firebaseFirestore) return;

    // Refuse to delete the last remaining project — the picker needs at least
    // one to render (matches Android).
    if (projects.length <= 1) {
      console.warn("Refusing to delete the last project:", projectId);
      return;
    }

    try {
      const uid = currentUser.uid;
      const pid = Number(projectId);
      const col = (name) =>
        collection(firebaseFirestore, `users/${uid}/${name}`);

      // Tasks and labels are scoped by projectId. Time records and actions are
      // scoped by the collected task ids — NOT projectId — because mobile-
      // created records/actions carry no projectId (Android's data model), so a
      // projectId query would leave them orphaned.
      const [taskSnap, labelSnap, recordSnap, actionSnap] = await Promise.all([
        getDocs(query(col("tasks"), where("projectId", "==", pid))),
        getDocs(query(col("labels"), where("projectId", "==", pid))),
        getDocs(col("time_records")),
        getDocs(col("actions")),
      ]);

      const taskIds = new Set(taskSnap.docs.map((d) => Number(d.data().id)));
      const inProject = (d) => taskIds.has(Number(d.data().taskId));
      const recordRefs = recordSnap.docs.filter(inProject).map((d) => d.ref);
      const actionRefs = actionSnap.docs.filter(inProject).map((d) => d.ref);

      const refs = [
        ...taskSnap.docs.map((d) => d.ref),
        ...recordRefs,
        ...labelSnap.docs.map((d) => d.ref),
        ...actionRefs,
        doc(firebaseFirestore, `users/${uid}/projects`, String(projectId)),
      ];

      // Firestore batches cap at 500 ops — chunk to stay under the limit.
      for (let i = 0; i < refs.length; i += 450) {
        const batch = writeBatch(firebaseFirestore);
        refs.slice(i, i + 450).forEach((ref) => batch.delete(ref));
        await batch.commit();
      }


      // If the deleted project was selected, fall back to a surviving project
      // (matches Android re-pointing the selection to the last survivor).
      if (String(activeProject?.id) === String(projectId)) {
        const survivor = projects.find(
          (p) => String(p.id) !== String(projectId)
        );
        if (survivor) handleProjectChange(survivor.id);
      }
    } catch (error) {
      console.error("Error deleting project:", error);
    }
  };

  const updateTask = async (taskId, updatedFields) => {
    if (!currentUser || !firebaseFirestore) return;

    try {
      // String() guard: Firestore doc() requires a string path segment, and a
      // task's id can arrive as the numeric `id` field. See utils/docToObject.
      const taskRef = doc(
        firebaseFirestore,
        `users/${currentUser.uid}/tasks`,
        String(taskId)
      );
      // Bump updatedAt so Android's last-write-wins sync ingests the edit.
      await updateDoc(taskRef, { ...updatedFields, updatedAt: Date.now() });
    } catch (error) {
      console.error("Error updating task:", error);
    }
  };

  const deleteTask = async (taskId) => {
    if (!currentUser || !firebaseFirestore) return;

    try {
      const taskRef = doc(
        firebaseFirestore,
        `users/${currentUser.uid}/tasks`,
        String(taskId)
      );
      await deleteDoc(taskRef);
    } catch (error) {
      console.error("Error deleting task:", error);
    }
  };

  // Delete a task and show an Undo snackbar (mirrors completeTask). Undo
  // re-creates the task doc with its original id + fields, so it reappears on
  // every device. Takes the full task object (from docToObject) so the snapshot
  // survives the delete.
  const deleteTaskWithUndo = async (task) => {
    if (!task || !currentUser || !firebaseFirestore) return;
    const snapshot = { ...task };
    await deleteTask(snapshot.id);
    showSnackbar({
      message: "Task deleted",
      actionLabel: "Undo",
      variant: "danger",
      onAction: async () => {
        const docId = toDocId(snapshot.id);
        await setDoc(
          doc(firebaseFirestore, `users/${currentUser.uid}/tasks`, docId),
          {
            ...snapshot,
            id: toRefValue(snapshot.id), // docToObject overwrote id with the doc-path string; restore numeric
            firestoreId: docId,
            remoteState: "remote_state_synced",
            updatedAt: Date.now(),
          }
        );
      },
    });
  };

  // Memoize this function to prevent it from changing on re-renders
  const fetchTimeRecords = useCallback(
    (taskId, callback) => {
      if (!currentUser || !firebaseFirestore) return;

      const timeRecordsRef = collection(
        firebaseFirestore,
        `users/${currentUser.uid}/time_records`
      );
      const timeRecordsQuery = query(
        timeRecordsRef,
        where(
          "taskId",
          "==",
          isNaN(taskId) ? taskId : Number(taskId) // If taskId is a number, convert it; otherwise, use it as a string
        )
      );

      const unsubscribe = onSnapshot(timeRecordsQuery, (snapshot) => {
        const timeRecords = snapshot.docs
          .map((doc) => docToObject(doc))
          // Newest first: most-recently-added record on top (startedTime is
          // stamped when the record is created). Sorted client-side so no
          // Firestore composite index is needed, matching fetchActions.
          .sort((a, b) => (b.startedTime || 0) - (a.startedTime || 0));
        callback(timeRecords);
      });

      return unsubscribe;
    },
    [currentUser, firebaseFirestore]
  );

  // Real-time listener for a task's actions (Android's `ActionData`). Mirrors
  // fetchTimeRecords: subscribes once per task via onSnapshot and reacts to
  // remote changes — no polling. Soft-deleted rows (remoteState ==
  // "remote_state_delete") are hidden and the list is ordered by actionOrder
  // ascending, matching the Android DAO query. Sorting/filtering is done
  // client-side so no Firestore composite index is required.
  const fetchActions = useCallback(
    (taskId, callback) => {
      if (!currentUser || !firebaseFirestore) return;

      const actionsRef = collection(
        firebaseFirestore,
        `users/${currentUser.uid}/actions`
      );
      const actionsQuery = query(
        actionsRef,
        where("taskId", "==", isNaN(taskId) ? taskId : Number(taskId))
      );

      const unsubscribe = onSnapshot(actionsQuery, (snapshot) => {
        const actions = snapshot.docs
          .map((doc) => docToObject(doc))
          .filter((action) => action.remoteState !== "remote_state_delete")
          .sort((a, b) => (a.actionOrder ?? 0) - (b.actionOrder ?? 0));
        callback(actions);
      });

      return unsubscribe;
    },
    [currentUser, firebaseFirestore]
  );

  // Update an action and bump updatedAt so Android's last-write-wins sync
  // ingests the edit. Writes remoteState "remote_state_synced" since the web
  // writes straight to the synced store (Firestore).
  const updateAction = async (actionId, updatedFields) => {
    if (!currentUser || !firebaseFirestore) return;

    try {
      const actionRef = doc(
        firebaseFirestore,
        `users/${currentUser.uid}/actions`,
        String(actionId)
      );
      await updateDoc(actionRef, {
        ...updatedFields,
        remoteState: "remote_state_synced",
        updatedAt: Date.now(),
      });
    } catch (error) {
      console.error("Error updating action:", error);
    }
  };

  // Create an action for a task. Mirrors Android's AddAction: appended at
  // maxOrder+1 with a numeric Int32 actionId so Room can ingest it. The caller
  // passes the task's current actions to compute the order and seed the id.
  const createAction = async (taskId, label, currentActions = []) => {
    if (!currentUser || !firebaseFirestore) return;

    const newId = generateNumericId(currentActions.map((a) => a.id));
    const order = nextActionOrder(currentActions);
    const action = buildAction(taskId, label, newId, order, Date.now());
    if (!action) return; // empty label → nothing to add

    const docId = toDocId(newId);
    await setDoc(
      doc(firebaseFirestore, `users/${currentUser.uid}/actions`, docId),
      action
    );
    return docId;
  };

  // Delete an action. Android soft-deletes in Room then its sync removes the
  // Firestore doc (batch.delete); the web writes straight to Firestore, so we
  // remove the doc directly — same end state. Sibling reindexing is handled by
  // the caller (TaskActionsContainer), matching MarkActionAsDelete.
  const deleteAction = async (actionId) => {
    if (!currentUser || !firebaseFirestore) return;
    try {
      await deleteDoc(
        doc(
          firebaseFirestore,
          `users/${currentUser.uid}/actions`,
          String(actionId)
        )
      );
    } catch (error) {
      console.error("Error deleting action:", error);
    }
  };

  // Convert an action into a task. Mirrors Android's startConvertingActionToTask:
  // a new task is created with the action's label, inheriting the parent task's
  // project and taskState; the caller then deletes the action. Returns the new
  // task's doc id (or undefined if the label was empty).
  const convertActionToTask = async (action, parentTask) => {
    if (!currentUser || !firebaseFirestore) return;

    const newId = generateNumericId(tasks.map((t) => t.id));
    const newTask = buildConvertedTask(action, parentTask, newId, Date.now());
    if (!newTask) return; // no parent or empty label → nothing to convert

    const docId = toDocId(newId);
    await setDoc(
      doc(firebaseFirestore, `users/${currentUser.uid}/tasks`, docId),
      newTask
    );
    return docId;
  };

  // Re-create a previously-deleted action (used by the convert "Undo"). Writes
  // the action's data back at its original doc id, refreshing the sync fields.
  const restoreAction = async (action) => {
    if (!currentUser || !firebaseFirestore || !action) return;
    const { id, ...data } = action; // `id` is the doc path, not a doc field
    await setDoc(
      doc(firebaseFirestore, `users/${currentUser.uid}/actions`, String(id)),
      { ...data, remoteState: "remote_state_synced", updatedAt: Date.now() }
    );
  };

  // ---- Labels (mirrors Android's label system) -------------------------

  // Real-time listener for the user's labels (cross-project; hides soft-deleted
  // rows, sorted by name). Callers filter by project scope client-side.
  const startCollectingLabels = () => {
    if (!currentUser || !firebaseFirestore) return;
    if (labelsUnsubscribeRef.current) labelsUnsubscribeRef.current();
    const labelsRef = collection(
      firebaseFirestore,
      `users/${currentUser.uid}/labels`
    );
    const unsubscribe = onSnapshot(labelsRef, (snapshot) => {
      const list = snapshot.docs
        .map(docToObject)
        .filter((l) => l.remoteState !== "remote_state_delete")
        .sort((a, b) =>
          (a.name || "").localeCompare(b.name || "", undefined, {
            sensitivity: "base",
          })
        );
      setLabels(list);
    });
    labelsUnsubscribeRef.current = unsubscribe;
  };

  // True if a label name already exists in the given scope (case-insensitive),
  // mirroring Android's nameExists* collision checks.
  const labelNameCollides = (name, projectId, ignoreId = null) => {
    const n = (name || "").trim().toLowerCase();
    return labels.some((l) => {
      if (String(l.id) === String(ignoreId)) return false;
      if ((l.name || "").trim().toLowerCase() !== n) return false;
      // global scope: any name anywhere collides; project scope: globals + that project.
      return projectId == null || l.projectId == null || l.projectId === projectId;
    });
  };

  // Create a label. Returns the new doc id, or null on empty name / collision.
  const createLabel = async (name, color, projectId = null) => {
    if (!currentUser || !firebaseFirestore) return null;
    if (!name || !name.trim()) return null;
    if (labelNameCollides(name, projectId)) return null;
    const newId = generateNumericId(labels.map((l) => l.id));
    const label = buildLabel(newId, name, color, projectId, Date.now());
    if (!label) return null;
    const docId = toDocId(newId);
    await setDoc(
      doc(firebaseFirestore, `users/${currentUser.uid}/labels`, docId),
      label
    );
    return docId;
  };

  // Update a label's fields (name/color/projectId). Bumps updatedAt. Returns
  // false on collision.
  const updateLabel = async (labelId, fields) => {
    if (!currentUser || !firebaseFirestore) return false;
    if ("name" in fields) {
      const projectId =
        "projectId" in fields
          ? fields.projectId
          : labels.find((l) => String(l.id) === String(labelId))?.projectId ??
            null;
      if (labelNameCollides(fields.name, projectId, labelId)) return false;
    }
    await updateDoc(
      doc(firebaseFirestore, `users/${currentUser.uid}/labels`, String(labelId)),
      {
        ...fields,
        ...(fields.name ? { name: fields.name.trim() } : {}),
        remoteState: "remote_state_synced",
        updatedAt: Date.now(),
      }
    );
    return true;
  };

  // Delete a label and scrub it from every task that references it (mirrors
  // Android's DeleteLabel eager cleanup).
  const deleteLabel = async (labelId) => {
    if (!currentUser || !firebaseFirestore) return;
    await deleteDoc(
      doc(firebaseFirestore, `users/${currentUser.uid}/labels`, String(labelId))
    );
    const numericId = Number(labelId);
    const snap = await getDocs(
      query(
        collection(firebaseFirestore, `users/${currentUser.uid}/tasks`),
        where("labelIds", "array-contains", numericId)
      )
    );
    for (const d of snap.docs) {
      const ids = (d.data().labelIds || []).filter((id) => id !== numericId);
      await updateDoc(d.ref, { labelIds: ids, updatedAt: Date.now() });
    }
  };

  // ── Action templates — reusable action checklists a label can auto-apply ───
  // Matches Android's ActionTemplateData (collection action_templates):
  // { templateId, name, actionLabels: [String], projectId | null, ... }
  const startCollectingActionTemplates = useCallback(() => {
    if (!currentUser || !firebaseFirestore) return;
    if (actionTemplatesUnsubscribeRef.current) actionTemplatesUnsubscribeRef.current();
    const ref = collection(firebaseFirestore, `users/${currentUser.uid}/action_templates`);
    actionTemplatesUnsubscribeRef.current = onSnapshot(ref, (snapshot) => {
      const list = snapshot.docs
        .map(docToObject)
        .filter((t) => t.remoteState !== "remote_state_delete")
        .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
      setActionTemplates(list);
    });
  }, [currentUser, firebaseFirestore]);

  const createActionTemplate = async (name, actionLabels = [], projectId = null) => {
    if (!currentUser || !firebaseFirestore) return null;
    const trimmed = (name || "").trim();
    if (!trimmed) return null;
    const newId = generateNumericId(actionTemplates.map((t) => t.id));
    const docId = toDocId(newId);
    await setDoc(doc(firebaseFirestore, `users/${currentUser.uid}/action_templates`, docId), {
      templateId: newId,
      name: trimmed,
      actionLabels: (actionLabels || []).map((s) => s.trim()).filter(Boolean),
      projectId: projectId == null ? null : toRefValue(projectId),
      remoteState: "remote_state_synced",
      firestoreId: docId,
      updatedAt: Date.now(),
    });
    return docId;
  };

  const updateActionTemplate = async (templateId, fields) => {
    if (!currentUser || !firebaseFirestore) return;
    const patch = { ...fields, remoteState: "remote_state_synced", updatedAt: Date.now() };
    if ("name" in patch) patch.name = (patch.name || "").trim();
    if ("actionLabels" in patch) patch.actionLabels = (patch.actionLabels || []).map((s) => s.trim()).filter(Boolean);
    await updateDoc(
      doc(firebaseFirestore, `users/${currentUser.uid}/action_templates`, String(templateId)),
      patch
    );
  };

  const deleteActionTemplate = async (templateId) => {
    if (!currentUser || !firebaseFirestore) return;
    await deleteDoc(doc(firebaseFirestore, `users/${currentUser.uid}/action_templates`, String(templateId)));
    // Clear the reference from any label that defaulted to it.
    const numericId = Number(templateId);
    for (const l of labels.filter((x) => Number(x.defaultActionTemplateId) === numericId)) {
      await updateDoc(
        doc(firebaseFirestore, `users/${currentUser.uid}/labels`, String(l.id)),
        { defaultActionTemplateId: null, remoteState: "remote_state_synced", updatedAt: Date.now() }
      );
    }
  };

  // Replace a task's label assignments. Stored as an array of numbers so
  // Android's List<Int> ingests it.
  const updateTaskLabels = async (taskId, labelIds) => {
    if (!currentUser || !firebaseFirestore) return;
    const numericIds = [...new Set(labelIds.map((id) => Number(id)))];
    await updateTask(taskId, { labelIds: numericIds });
  };

  const addTimeRecord = async (
    taskId,
    projectId,
    spentTime,
    trackerKind = "human",
    actionId = null
  ) => {
    if (!currentUser || !firebaseFirestore) return;

    try {
      // Numeric, Int32-safe id + Android (Room) TimeRecordData wire format.
      // `projectId` is web-only (Android's TimeRecordData has no such field)
      // but kept as a number for web's per-project queries. See utils/ids.js.
      // `trackerKind` is "human" or "ai" (drives AI billing + the AI Hours stat).
      // `actionId` links the record to the specific action that was tracked
      // (Android TimeRecordData.actionId), or null for whole-task tracking.
      const newId = generateNumericId();
      const docId = toDocId(newId);

      const record = {
        id: newId,
        taskId: toRefValue(taskId),
        projectId: toRefValue(projectId),
        spentTime: spentTime,
        startedTime: Date.now(),
        trackerKind: trackerKind,
        actionId: actionId == null ? null : toRefValue(actionId),
        remoteState: "remote_state_synced",
        firestoreId: docId,
        updatedAt: Date.now(),
      };

      await setDoc(
        doc(firebaseFirestore, `users/${currentUser.uid}/time_records`, docId),
        record
      );

    } catch (error) {
      console.error("Error adding time record:", error);
    }
  };

  // fields: { spentTime?, startedTime?, trackerKind? } — the record fields the
  // edit dialog can change (duration like Android/web, start timestamp like
  // iOS, human/ai kind like Android).
  const updateTimeRecord = async (recordId, fields) => {
    if (!firebaseFirestore || !currentUser) return;

    try {
      const recordRef = doc(
        firebaseFirestore,
        `users/${currentUser.uid}/time_records`,
        recordId
      );
      await updateDoc(recordRef, {
        ...fields,
        updatedAt: Date.now(),
      });
    } catch (error) {
      console.error(`Error updating time record ${recordId}:`, error);
    }
  };

  const setTaskState = async (taskId, newTaskState) => {
    if (!currentUser || !firebaseFirestore) return;
    try {
      const taskRef = doc(
        firebaseFirestore,
        `users/${currentUser.uid}/tasks`,
        String(taskId) // Ensure taskId is a string
      );
      await updateDoc(taskRef, {
        taskState: newTaskState,
        updatedAt: Date.now(),
      });
    } catch (error) {
      console.error("Error updating task state:", error);
    }
  };

  const markTaskAsDone = async (taskId) => {
    await updateTask(taskId, {
      taskState: "task_state_done",
      taskBeingTracked: false,
    });
    await updateRecordingTaskInFirestore(null);
    setRecordingTaskId(null);
    setLastRecordedTask(null);
  };

  // Central "mark a task completed" path. Every place that completes a task
  // (card done toggle, task detail) routes through here so the Android-style
  // "Marked as done" undo snackbar appears consistently. Mirrors Android's
  // ToggleTaskStateDone: stops tracking, and the Undo reverts to the task's
  // PREVIOUS state (backlog or WIP), not unconditionally to backlog.
  const completeTask = async (task) => {
    if (!task) return;
    const previousState = task.taskState || "task_state_backlog";
    const previousLastTrackedAt = task.lastTrackedAt ?? null;

    // Stop any in-progress recording started by this task and BANK the elapsed
    // time before marking it done — otherwise completing via the checkbox would
    // silently drop the running session. We re-read the live doc so the
    // detail-screen flow (which already stopped via setTaskTrackingState) won't
    // double-record: by then trackStartedTimestamp is already null.
    if (currentUser && firebaseFirestore) {
      const ref = doc(firebaseFirestore, `users/${currentUser.uid}/tasks`, String(task.id));
      const fresh = (await getDoc(ref)).data() || {};
      const pid = fresh.projectId ?? task.projectId;
      if (fresh.taskBeingTracked && fresh.trackStartedTimestamp) {
        await addTimeRecord(
          task.id,
          pid,
          Date.now() - fresh.trackStartedTimestamp,
          "human",
          fresh.trackedActionId ?? null
        );
      }
      if (fresh.aiBeingTracked && fresh.aiTrackStartedTimestamp) {
        await addTimeRecord(
          task.id,
          pid,
          Date.now() - fresh.aiTrackStartedTimestamp,
          "ai",
          fresh.aiTrackedActionId ?? null
        );
      }
    }

    await updateTask(task.id, {
      taskState: "task_state_done",
      taskBeingTracked: false,
      // Drop the task from the tracking tray on completion. The tray lists
      // tasks with lastTrackedAt != null (active + recents), so clearing it
      // removes this row; the tracking fields are reset to match the no-longer-
      // tracked state. (Elapsed time was already banked above.)
      lastTrackedAt: null,
      trackStartedTimestamp: null,
      trackingDeviceId: null,
      trackedActionId: null,
      // Stop the AI tracker too (both kinds end when the task is completed).
      aiBeingTracked: false,
      aiTrackStartedTimestamp: null,
      aiTrackingDeviceId: null,
      aiTrackedActionId: null,
    });

    // Only clear the recording pointer if THIS task was the one recording.
    if (recordingTaskId && String(recordingTaskId) === String(task.id)) {
      await updateRecordingTaskInFirestore(null);
      setRecordingTaskId(null);
      setLastRecordedTask(null);
    }

    showSnackbar({
      message: "Marked as done",
      actionLabel: "Undo",
      variant: "success",
      // Undo restores the prior state and, if the task had been in the tracking
      // tray (recents), its lastTrackedAt so the row reappears.
      onAction: () =>
        updateTask(task.id, {
          taskState: previousState,
          lastTrackedAt: previousLastTrackedAt,
        }),
    });
  };

  // Stop every OTHER task this device currently has running. Used in single-
  // tracking mode (multi-tracking off) before starting a new timer — mirrors
  // Android's findCurrentlyTrackedTaskOwnedBy. Foreign trackers (started on
  // another device) are intentionally left alone.
  const stopOtherOwnedActiveTasks = async (exceptTaskId) => {
    if (!currentUser || !firebaseFirestore) return;
    const deviceId = getDeviceId();
    const snap = await getDocs(
      query(
        collection(firebaseFirestore, `users/${currentUser.uid}/tasks`),
        where("taskBeingTracked", "==", true)
      )
    );
    for (const d of snap.docs) {
      const data = d.data();
      if (d.id !== String(exceptTaskId) && data.trackingDeviceId === deviceId) {
        await setTaskTrackingState(d.id, data.projectId, false);
      }
    }
  };

  // `actionId` (optional) scopes the human timer to a specific action
  // (Android's trackedActionId). null = whole-task tracking.
  const setTaskTrackingState = async (
    taskId,
    projectId,
    newTrackingState,
    actionId = null
  ) => {
    if (!currentUser || !firebaseFirestore) return;
    try {
      const taskRef = doc(
        firebaseFirestore,
        `users/${currentUser.uid}/tasks`,
        String(taskId)
      );
      if (!newTrackingState) {
        // Stop: record the elapsed time (attributed to the tracked action, if
        // any), then clear the tracking fields. trackStartedTimestamp may belong
        // to another device (foreign stop) — we still use it so the recorded
        // duration is correct. lastTrackedAt is deliberately NOT cleared.
        const taskDoc = await getDoc(taskRef);
        const taskData = taskDoc.data();
        if (taskData && taskData.trackStartedTimestamp) {
          const spentTime = Date.now() - taskData.trackStartedTimestamp;
          await addTimeRecord(
            taskId,
            projectId,
            spentTime,
            "human",
            taskData.trackedActionId ?? null
          );
        }

        await updateDoc(taskRef, {
          taskBeingTracked: false,
          trackStartedTimestamp: null,
          trackingDeviceId: null,
          trackedActionId: null,
          updatedAt: Date.now(),
        });
      } else {
        // Start. If this task is already tracking (a different action or the
        // whole task), bank that segment first so switching actions never drops
        // time. Then, in single-tracking mode, stop this device's other timers.
        const existing = (await getDoc(taskRef)).data();
        if (existing && existing.taskBeingTracked && existing.trackStartedTimestamp) {
          const spent = Date.now() - existing.trackStartedTimestamp;
          await addTimeRecord(
            taskId,
            projectId,
            spent,
            "human",
            existing.trackedActionId ?? null
          );
        }
        if (!isMultiTrackingEnabled()) {
          await stopOtherOwnedActiveTasks(taskId);
        }
        const now = Date.now();
        await updateDoc(taskRef, {
          taskBeingTracked: true,
          taskState: "task_state_wip",
          trackStartedTimestamp: now,
          trackingDeviceId: getDeviceId(), // stamp ownership
          trackedActionId: actionId == null ? null : toRefValue(actionId),
          lastTrackedAt: now, // recents ordering source
          updatedAt: now,
        });
      }
    } catch (error) {
      console.error("Error updating task tracking state:", error);
    }
  };

  // AI tracking is a SECOND, independent tracker per task (Android's TrackerKind
  // model): its own aiBeingTracked / aiTrackStartedTimestamp / aiTrackingDeviceId
  // fields and its own time records (trackerKind="ai"). A task can run both the
  // human and AI timers at once.
  const stopOtherOwnedAiTasks = async (exceptTaskId) => {
    if (!currentUser || !firebaseFirestore) return;
    const deviceId = getDeviceId();
    const snap = await getDocs(
      query(
        collection(firebaseFirestore, `users/${currentUser.uid}/tasks`),
        where("aiBeingTracked", "==", true)
      )
    );
    for (const d of snap.docs) {
      const data = d.data();
      if (d.id !== String(exceptTaskId) && data.aiTrackingDeviceId === deviceId) {
        await setTaskAiTrackingState(d.id, data.projectId, false);
      }
    }
  };

  const setTaskAiTrackingState = async (
    taskId,
    projectId,
    newTrackingState,
    actionId = null
  ) => {
    if (!currentUser || !firebaseFirestore) return;
    try {
      const taskRef = doc(
        firebaseFirestore,
        `users/${currentUser.uid}/tasks`,
        String(taskId)
      );
      if (!newTrackingState) {
        // Stop: bank the elapsed AI time (attributed to its action), clear fields.
        const taskDoc = await getDoc(taskRef);
        const taskData = taskDoc.data();
        if (taskData && taskData.aiTrackStartedTimestamp) {
          const spentTime = Date.now() - taskData.aiTrackStartedTimestamp;
          await addTimeRecord(
            taskId,
            projectId,
            spentTime,
            "ai",
            taskData.aiTrackedActionId ?? null
          );
        }
        await updateDoc(taskRef, {
          aiBeingTracked: false,
          aiTrackStartedTimestamp: null,
          aiTrackingDeviceId: null,
          aiTrackedActionId: null,
          updatedAt: Date.now(),
        });
      } else {
        const existing = (await getDoc(taskRef)).data();
        if (
          existing &&
          existing.aiBeingTracked &&
          existing.aiTrackStartedTimestamp
        ) {
          const spent = Date.now() - existing.aiTrackStartedTimestamp;
          await addTimeRecord(
            taskId,
            projectId,
            spent,
            "ai",
            existing.aiTrackedActionId ?? null
          );
        }
        if (!isMultiTrackingEnabled()) {
          await stopOtherOwnedAiTasks(taskId);
        }
        const now = Date.now();
        await updateDoc(taskRef, {
          aiBeingTracked: true,
          taskState: "task_state_wip",
          aiTrackStartedTimestamp: now,
          aiTrackingDeviceId: getDeviceId(),
          aiTrackedActionId: actionId == null ? null : toRefValue(actionId),
          lastTrackedAt: now, // shares the recents ordering with human tracking
          updatedAt: now,
        });
      }
    } catch (error) {
      console.error("Error updating AI tracking state:", error);
    }
  };

  // Finalize (stop + record) any timer currently attributed to `actionId`.
  // Reads the LIVE task doc rather than trusting a possibly-stale in-memory
  // task, so checking an action right after starting its timer still banks the
  // elapsed time — the bug where the checkbox stopped tracking without creating
  // a record. No-op for a timer tied to a different action or the whole task.
  // Mirrors iOS/Android, where checking an action closes out its recording.
  const finalizeActionTimers = async (taskId, projectId, actionId) => {
    if (!currentUser || !firebaseFirestore || actionId == null) return;
    try {
      const taskRef = doc(
        firebaseFirestore,
        `users/${currentUser.uid}/tasks`,
        String(taskId)
      );
      const data = (await getDoc(taskRef)).data();
      if (!data) return;
      const aId = Number(actionId);
      if (data.taskBeingTracked && Number(data.trackedActionId) === aId) {
        await setTaskTrackingState(taskId, projectId, false, actionId);
      }
      if (data.aiBeingTracked && Number(data.aiTrackedActionId) === aId) {
        await setTaskAiTrackingState(taskId, projectId, false, actionId);
      }
    } catch (error) {
      console.error("Error finalizing action timers:", error);
    }
  };

  // Real-time listener for the tracking tray: every task currently being
  // tracked (cross-project), ordered most-recent first and capped at 5,
  // mirroring Android's TRACKED_TRAY_CAP.
  const startCollectingTrackedTasks = () => {
    if (!currentUser || !firebaseFirestore) return;
    if (trackedTasksUnsubscribeRef.current) {
      trackedTasksUnsubscribeRef.current();
    }
    // Tray = active (running) ∪ recent (ever-tracked), cross-project, capped at
    // 5. lastTrackedAt is set on every start and NEVER cleared on stop, so a
    // stopped task lingers as a "recent" until the user dismisses it (which
    // nulls lastTrackedAt). Mirrors Android's ActiveTrackedTaskUiModelsFlow.
    const trackedQuery = query(
      collection(firebaseFirestore, `users/${currentUser.uid}/tasks`),
      orderBy("lastTrackedAt", "desc"),
      limit(8)
    );
    const unsubscribe = onSnapshot(trackedQuery, (snapshot) => {
      const withRecency = snapshot.docs
        .map(docToObject)
        .filter((t) => t.lastTrackedAt != null);

      // A completed task must never sit in the tray. If one is found (e.g.
      // finished on another device, which doesn't clear lastTrackedAt), forget
      // it permanently by nulling lastTrackedAt so it drops out for good.
      withRecency
        .filter((t) => t.taskState === "task_state_done")
        .forEach((t) => updateTask(t.id, { lastTrackedAt: null }));

      // Tray only shows WIP tasks — tracking moves a task into WIP, and a task
      // that's left WIP (Done, or manually moved to backlog) doesn't belong.
      const all = withRecency.filter(
        (t) => t.taskState === "task_state_wip"
      );
      // A task is "active" if EITHER tracker is running (human or AI).
      const isActive = (t) => t.taskBeingTracked || t.aiBeingTracked;
      const active = all.filter(isActive);
      const recent = all.filter((t) => !isActive(t));
      // Active first (never dropped), then recents, capped at 5.
      setTrackedTasks([...active, ...recent].slice(0, 5));
    });
    trackedTasksUnsubscribeRef.current = unsubscribe;
  };

  // Dismiss a task from the tray: clear lastTrackedAt so it drops out of the
  // "recent" set (Android's "remove from tray").
  const dismissTrackedTask = (taskId) => {
    updateTask(taskId, { lastTrackedAt: null });
  };

  // Total time spent on a task = sum of its time records' spentTime (ms). Used
  // by the tray's "Total Spent" label for stopped tasks.
  const getTaskTotalSpent = async (taskId) => {
    if (!currentUser || !firebaseFirestore) return 0;
    const snap = await getDocs(
      query(
        collection(firebaseFirestore, `users/${currentUser.uid}/time_records`),
        where("taskId", "==", Number(taskId))
      )
    );
    return snap.docs.reduce((sum, d) => sum + (d.data().spentTime || 0), 0);
  };

  // The label of a single action (for the tray's "<task> · <action>" title).
  const getActionLabel = async (actionId) => {
    if (!currentUser || !firebaseFirestore || actionId == null) return "";
    const snap = await getDoc(
      doc(firebaseFirestore, `users/${currentUser.uid}/actions`, String(actionId))
    );
    return snap.exists() ? snap.data().actionLabel || "" : "";
  };

  // Aggregate tracking/action stats for a set of task ids (a project's tasks).
  // Scoped by taskId membership — NOT by time_records.projectId — because that
  // field's stored type (number) and the existing listener's query type
  // (string) disagree; task ids are written consistently, so this is robust.
  // One-shot read; callers refresh it when the task set changes.
  const getProjectStats = async (taskIds = []) => {
    const empty = {
      totalSpentTime: 0,
      aiSpentTime: 0,
      sessionCount: 0,
      totalActions: 0,
    };
    if (!currentUser || !firebaseFirestore || taskIds.length === 0) return empty;

    const idSet = new Set(taskIds.map(Number));
    const [recordSnap, actionSnap] = await Promise.all([
      getDocs(collection(firebaseFirestore, `users/${currentUser.uid}/time_records`)),
      getDocs(collection(firebaseFirestore, `users/${currentUser.uid}/actions`)),
    ]);

    const result = { ...empty };
    recordSnap.docs.forEach((d) => {
      const r = d.data();
      if (!idSet.has(Number(r.taskId))) return;
      const spent = r.spentTime || 0;
      result.totalSpentTime += spent;
      result.sessionCount += 1;
      if (r.trackerKind === "ai") result.aiSpentTime += spent; // 0 on web
    });
    actionSnap.docs.forEach((d) => {
      const a = d.data();
      if (a.remoteState === "remote_state_delete") return;
      if (idSet.has(Number(a.taskId))) result.totalActions += 1;
    });
    return result;
  };

  // Stop a tracker from the tray. If it was started on another device, confirm
  // via the Android-style "Stop anyway" danger snackbar before stopping.
  const requestStopTracking = (task) => {
    const isForeign =
      task.taskBeingTracked &&
      task.trackingDeviceId &&
      task.trackingDeviceId !== getDeviceId();
    if (isForeign) {
      showSnackbar({
        message: "Tracker started on another device. Stop here anyway?",
        actionLabel: "Stop anyway",
        variant: "danger",
        onAction: () =>
          setTaskTrackingState(task.id, task.projectId, false),
      });
    } else {
      setTaskTrackingState(task.id, task.projectId, false);
    }
  };

  // When the user turns multi-tracking OFF, stop all but the most-recently
  // started owned timer and notify — mirrors Android's UpdateMultiTrackingEnabled.
  const enforceSingleTracking = async () => {
    if (!currentUser || !firebaseFirestore) return;
    const deviceId = getDeviceId();
    const snap = await getDocs(
      query(
        collection(firebaseFirestore, `users/${currentUser.uid}/tasks`),
        where("taskBeingTracked", "==", true)
      )
    );
    const owned = snap.docs
      .map(docToObject)
      .filter((t) => t.trackingDeviceId === deviceId)
      .sort(
        (a, b) => (b.trackStartedTimestamp ?? 0) - (a.trackStartedTimestamp ?? 0)
      );
    if (owned.length > 1) {
      for (const task of owned.slice(1)) {
        await setTaskTrackingState(task.id, task.projectId, false);
      }
      showSnackbar({
        message: "Multi-tracking off. Kept the latest task, stopped the rest.",
        variant: "success",
      });
    }
  };

  const deleteTimeRecord = async (timeRecordId) => {
    if (!currentUser || !firebaseFirestore) return;

    try {
      const timeRecordRef = doc(
        firebaseFirestore,
        `users/${currentUser.uid}/time_records`,
        timeRecordId
      );
      await deleteDoc(timeRecordRef);
    } catch (error) {
      console.error("Error deleting time record:", error);
    }
  };

  const startCollectingWipTasks = () => {
    if (!currentUser || !firebaseFirestore) return;

    // Unsubscribe from the previous listener if it exists
    if (wipTasksUnsubscribeRef.current) {
      wipTasksUnsubscribeRef.current();
    }

    const userTasksRef = collection(
      firebaseFirestore,
      `users/${currentUser.uid}/tasks`
    );
    const wipTasksQuery = query(
      userTasksRef,
      where("taskState", "==", "task_state_wip")
    );


    const unsubscribeWipTasks = onSnapshot(wipTasksQuery, (snapshot) => {
      const wipTaskList = snapshot.docs.map((doc) => docToObject(doc));
      setWipTasks(wipTaskList);
    });

    // Store the unsubscribe function in the ref to allow future cleanup
    wipTasksUnsubscribeRef.current = unsubscribeWipTasks;
  };

  const startCollectingTaskById = (taskId) => {
    if (!currentUser || !firebaseFirestore || !taskId) return;

    if (taskDetailUnsubscribeRef.current) {
      taskDetailUnsubscribeRef.current();
    }

    const taskDocRef = doc(
      firebaseFirestore,
      `users/${currentUser.uid}/tasks`,
      taskId
    );


    const unsubscribeTaskDetail = onSnapshot(taskDocRef, (doc) => {
      if (doc.exists()) {
        setTaskDetail(docToObject(doc));
      } else {
        setTaskDetail(null);
      }
    });

    taskDetailUnsubscribeRef.current = unsubscribeTaskDetail;
  };

  const updateTaskDetail = async (taskId, updatedFields) => {
    if (!currentUser || !firebaseFirestore || !taskId) return;

    try {
      const taskRef = doc(
        firebaseFirestore,
        `users/${currentUser.uid}/tasks`,
        taskId
      );
      // Bump updatedAt so Android's last-write-wins sync ingests the edit.
      await updateDoc(taskRef, { ...updatedFields, updatedAt: Date.now() });
    } catch (error) {
      console.error("Error updating task detail:", error);
    }
  };

  const startCollectingTimeRecords = (projectId = activeProject?.id) => {
    if (!currentUser || !firebaseFirestore) return;

    if (!projectId) {
      console.error("No projectId provided and no active project found.");
      return;
    }


    // Unsubscribe the previous listener if it exists
    if (timeRecordsUnsubscribeRef.current) {
      timeRecordsUnsubscribeRef.current();
      timeRecordsUnsubscribeRef.current = null;
    }

    // Listen to the WHOLE time_records collection (no projectId filter). Records
    // are scoped to the active project by taskId in the `projectTimeRecords`
    // derivation below — this is the only field shared with Android-created
    // records, which carry no projectId. For a single user this is a few
    // hundred docs, so an unfiltered listener is cheap and always complete.
    const timeRecordsRef = collection(
      firebaseFirestore,
      `users/${currentUser.uid}/time_records`
    );


    const unsubscribeTimeRecords = onSnapshot(timeRecordsRef, (snapshot) => {
      const timeRecordsList = snapshot.docs.map((doc) => docToObject(doc));
      setAllTimeRecords(timeRecordsList);
    });

    // Store the unsubscribe function in the ref
    timeRecordsUnsubscribeRef.current = unsubscribeTimeRecords;
  };

  const startCollectingTodayTimeRecords = () => {
    if (!currentUser || !firebaseFirestore) return;

    // Unsubscribe from the previous listener if it exists
    if (todayTimeRecordsUnsubscribeRef.current) {
      todayTimeRecordsUnsubscribeRef.current();
    }

    const timeRecordsRef = collection(
      firebaseFirestore,
      `users/${currentUser.uid}/time_records`
    );

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0); // Set time to 00:00:00
    const endOfToday = new Date();
    endOfToday.setHours(23, 59, 59, 999); // Set time to 23:59:59

    const todaysRecordsQuery = query(
      timeRecordsRef,
      where("startedTime", ">=", startOfToday.getTime()),
      where("startedTime", "<=", endOfToday.getTime())
    );

    const unsubscribe = onSnapshot(todaysRecordsQuery, (snapshot) => {
      const todaysTimeRecords = snapshot.docs.map((doc) => docToObject(doc));

      setTodayTimeRecords(todaysTimeRecords);
    });

    // Store the unsubscribe function in the ref to allow future cleanup
    todayTimeRecordsUnsubscribeRef.current = unsubscribe;
  };

  const startCollectingTodayTimeRecordsByProject = (
    projectId = activeProject?.id
  ) => {
    if (!firebaseFirestore || !currentUser) return;

    // Unsubscribe from the previous listener if it exists
    if (todayTimeRecordsByProjectUnsubscribeRef.current) {
      todayTimeRecordsByProjectUnsubscribeRef.current();
    }

    // Call the use case function and save the unsubscribe function
    todayTimeRecordsByProjectUnsubscribeRef.current =
      startCollectingTodayTimeRecordsByProjectUseCase({
        firebaseFirestore,
        currentUser,
        projectId,
        setTodayTimeRecordsByProject,
      });
  };

  const getFreshToken = async () => {
    if (currentUser) {
      return await currentUser.getIdToken(true); // Get a fresh token
    }
    throw new Error("User is not authenticated");
  };

  /**
   * DEV-ONLY one-off cleanup. Deletes any doc whose id is NOT a plain integer
   * string — i.e. the random Firestore auto-ids ("aBc123XyZ") that the old web
   * code created and that the Android (Room Int) app can't ingest. Android's
   * numeric-id docs are left untouched. Run once from the browser console:
   *   await window.wipeIncompatibleDocs()
   * Returns a per-collection count of deleted docs.
   */
  const wipeIncompatibleDocs = async () => {
    if (!currentUser || !firebaseFirestore) {
      throw new Error("Not signed in / Firestore not ready.");
    }
    const collections = ["projects", "tasks", "time_records"];
    const isNumericId = (id) => /^\d+$/.test(id);
    const deleted = {};
    for (const name of collections) {
      const snap = await getDocs(
        collection(firebaseFirestore, `users/${currentUser.uid}/${name}`)
      );
      const bad = snap.docs.filter((d) => !isNumericId(d.id));
      for (const d of bad) await deleteDoc(d.ref);
      deleted[name] = bad.length;
    }
    return deleted;
  };
  // Expose the dev cleanup on window so it can be run from the browser
  // console (assigned during render — dev-only, harmless to repeat).
  if (process.env.NODE_ENV !== "production" && typeof window !== "undefined") {
    window.wipeIncompatibleDocs = wipeIncompatibleDocs;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Recurring rules + notification inbox — web mirror of the Android system.
  // recurring_rules: full CRUD. notifications: inbox feed (read/dismiss).
  // Materialization runs client-side while the app is open, claimed via a
  // Firestore transaction so web + Android can't double-create a task.
  // ─────────────────────────────────────────────────────────────────────────

  const startCollectingRecurringRules = useCallback(() => {
    if (!currentUser || !firebaseFirestore) return;
    if (recurringRulesUnsubscribeRef.current) recurringRulesUnsubscribeRef.current();
    const ref = collection(firebaseFirestore, `users/${currentUser.uid}/recurring_rules`);
    recurringRulesUnsubscribeRef.current = onSnapshot(ref, (snapshot) => {
      const list = snapshot.docs
        .map(docToObject)
        .filter((r) => r.remoteState !== REMOTE_STATE_DELETE)
        .sort((a, b) => (a.nextFireAt || 0) - (b.nextFireAt || 0));
      setRecurringRules(list);
    });
  }, [currentUser, firebaseFirestore]);

  const startCollectingNotifications = useCallback(() => {
    if (!currentUser || !firebaseFirestore) return;
    if (notificationsUnsubscribeRef.current) notificationsUnsubscribeRef.current();
    const ref = collection(firebaseFirestore, `users/${currentUser.uid}/notifications`);
    notificationsUnsubscribeRef.current = onSnapshot(ref, (snapshot) => {
      const list = snapshot.docs
        .map(docToObject)
        .filter((n) => n.remoteState !== REMOTE_STATE_DELETE)
        .sort((a, b) => (b.firedAt || 0) - (a.firedAt || 0));
      setNotifications(list);
    });
  }, [currentUser, firebaseFirestore]);

  // ─────────────────────────────────────────────────────────────────────────
  // Invoices + Clients (Android InvoiceData / ClientData wire format)
  // ─────────────────────────────────────────────────────────────────────────
  const startCollectingInvoices = useCallback(() => {
    if (!currentUser || !firebaseFirestore) return;
    if (invoicesUnsubscribeRef.current) invoicesUnsubscribeRef.current();
    const ref = collection(firebaseFirestore, `users/${currentUser.uid}/invoices`);
    invoicesUnsubscribeRef.current = onSnapshot(ref, (snapshot) => {
      const list = snapshot.docs
        .map(docToObject)
        .filter((i) => i.remoteState !== REMOTE_STATE_DELETE)
        // Newest first: issueDate desc, then number desc (matches Android list order).
        .sort((a, b) => (b.issueDate || 0) - (a.issueDate || 0) || (b.number || 0) - (a.number || 0));
      setInvoices(list);
    });
  }, [currentUser, firebaseFirestore]);

  const startCollectingClients = useCallback(() => {
    if (!currentUser || !firebaseFirestore) return;
    if (clientsUnsubscribeRef.current) clientsUnsubscribeRef.current();
    const ref = collection(firebaseFirestore, `users/${currentUser.uid}/clients`);
    clientsUnsubscribeRef.current = onSnapshot(ref, (snapshot) => {
      const list = snapshot.docs
        .map(docToObject)
        .filter((c) => c.remoteState !== REMOTE_STATE_DELETE)
        .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
      setClients(list);
    });
  }, [currentUser, firebaseFirestore]);

  const nextInvoiceNumber = () =>
    invoices.reduce((max, i) => Math.max(max, Number(i.number) || 0), 0) + 1;

  // Persist an invoice doc. `data` carries Android InvoiceData fields; we stamp
  // the numeric id, sync fields, and updatedAt. Used by create + duplicate.
  const writeInvoice = async (id, data) => {
    const docId = toDocId(id);
    const payload = {
      invoiceId: id,
      number: Number(data.number) || 0,
      clientId: Number(data.clientId) || 0,
      projectIds: (data.projectIds || []).map(Number),
      periodStart: data.periodStart || 0,
      periodEnd: data.periodEnd || 0,
      issueDate: data.issueDate || 0,
      dueDate: data.dueDate || 0,
      taxRatePercent: Number(data.taxRatePercent) || 0,
      notes: data.notes || "",
      paid: !!data.paid,
      createdAt: data.createdAt || Date.now(),
      updatedAt: Date.now(),
      remoteState: REMOTE_STATE_SYNCED,
      firestoreId: docId,
    };
    await setDoc(doc(firebaseFirestore, `users/${currentUser.uid}/invoices`, docId), payload);
    return docId;
  };

  const createInvoice = async () => {
    if (!currentUser || !firebaseFirestore) return null;
    const now = Date.now();
    const DAY = 24 * 60 * 60 * 1000;
    const newId = generateNumericId(invoices.map((i) => i.id));
    return writeInvoice(newId, {
      number: nextInvoiceNumber(),
      clientId: 0,
      projectIds: [],
      periodStart: now - 30 * DAY,
      periodEnd: now,
      issueDate: now,
      dueDate: now + 15 * DAY,
      taxRatePercent: 0,
      notes: "",
      paid: false,
      createdAt: now,
    });
  };

  const duplicateLastInvoice = async () => {
    if (!currentUser || !firebaseFirestore || invoices.length === 0) return null;
    const now = Date.now();
    const DAY = 24 * 60 * 60 * 1000;
    const last = invoices[0]; // list is sorted newest-first
    const newId = generateNumericId(invoices.map((i) => i.id));
    return writeInvoice(newId, {
      number: nextInvoiceNumber(),
      clientId: last.clientId || 0,
      projectIds: last.projectIds || [],
      periodStart: last.periodStart || now - 30 * DAY,
      periodEnd: last.periodEnd || now,
      issueDate: now,
      dueDate: now + 15 * DAY,
      taxRatePercent: last.taxRatePercent || 0,
      notes: last.notes || "",
      paid: false,
      createdAt: now,
    });
  };

  const updateInvoice = async (invoiceId, fields) => {
    if (!currentUser || !firebaseFirestore) return;
    await updateDoc(
      doc(firebaseFirestore, `users/${currentUser.uid}/invoices`, String(invoiceId)),
      { ...fields, remoteState: REMOTE_STATE_SYNCED, updatedAt: Date.now() }
    );
  };

  const deleteInvoice = async (invoiceId) => {
    if (!currentUser || !firebaseFirestore) return;
    await deleteDoc(doc(firebaseFirestore, `users/${currentUser.uid}/invoices`, String(invoiceId)));
  };

  const createClient = async (name = "", address = "", taxId = "") => {
    if (!currentUser || !firebaseFirestore) return null;
    const now = Date.now();
    const newId = generateNumericId(clients.map((c) => c.id));
    const docId = toDocId(newId);
    await setDoc(doc(firebaseFirestore, `users/${currentUser.uid}/clients`, docId), {
      clientId: newId,
      name: (name || "").trim(),
      address: address || "",
      taxId: taxId || "",
      createdAt: now,
      updatedAt: now,
      remoteState: REMOTE_STATE_SYNCED,
      firestoreId: docId,
    });
    return docId;
  };

  const updateClient = async (clientId, fields) => {
    if (!currentUser || !firebaseFirestore) return;
    await updateDoc(
      doc(firebaseFirestore, `users/${currentUser.uid}/clients`, String(clientId)),
      { ...fields, remoteState: REMOTE_STATE_SYNCED, updatedAt: Date.now() }
    );
  };

  const deleteClient = async (clientId) => {
    if (!currentUser || !firebaseFirestore) return;
    await deleteDoc(doc(firebaseFirestore, `users/${currentUser.uid}/clients`, String(clientId)));
  };

  // Whole-account tasks + time records for invoice line-item computation. The
  // live `tasks` state is scoped to the active project, but an invoice can span
  // several projects, so we read the full collections on demand here.
  const getInvoiceSourceData = useCallback(async () => {
    if (!currentUser || !firebaseFirestore) return { tasks: [], timeRecords: [] };
    const [taskSnap, trSnap] = await Promise.all([
      getDocs(collection(firebaseFirestore, `users/${currentUser.uid}/tasks`)),
      getDocs(collection(firebaseFirestore, `users/${currentUser.uid}/time_records`)),
    ]);
    return {
      tasks: taskSnap.docs.map(docToObject),
      timeRecords: trSnap.docs.map(docToObject),
    };
  }, [currentUser, firebaseFirestore]);

  const createRecurringRule = async () => {
    if (!currentUser || !firebaseFirestore) return null;
    const target =
      activeProject && !isAllProjects(activeProject.id) ? activeProject : projects[0];
    const newId = generateNumericId(recurringRules.map((r) => r.id));
    const docId = toDocId(newId);
    const rule = {
      id: newId,
      title: "",
      projectId: toRefValue(target ? target.id : 0),
      recurrenceType: "MONTHLY",
      dayOfMonth: 1,
      dayOfWeek: 2, // Calendar.MONDAY
      hour: 9,
      minute: 0,
      targetState: TASK_STATE_BACKLOG,
      enabled: false, // a fresh rule is a paused draft, like Android
      setDueDate: false,
      labelIds: [],
      nextFireAt: 0,
      lastFiredAt: null,
      remoteState: REMOTE_STATE_SYNCED,
      firestoreId: docId,
      updatedAt: Date.now(),
    };
    await setDoc(doc(firebaseFirestore, `users/${currentUser.uid}/recurring_rules`, docId), rule);
    return { id: docId, ...rule };
  };

  const updateRecurringRule = async (firestoreId, fields) => {
    if (!currentUser || !firebaseFirestore) return;
    await updateDoc(
      doc(firebaseFirestore, `users/${currentUser.uid}/recurring_rules`, String(firestoreId)),
      { ...fields, updatedAt: Date.now() }
    );
  };

  const toggleRecurringRule = async (rule) => {
    const enabled = !rule.enabled;
    const nextFireAt = enabled
      ? nextFireAfter(rule.recurrenceType, rule.dayOfWeek, rule.dayOfMonth, rule.hour, rule.minute, Date.now())
      : rule.nextFireAt;
    await updateRecurringRule(rule.firestoreId || rule.id, { enabled, nextFireAt });
  };

  const deleteRecurringRule = async (rule) => {
    // Soft-delete so the removal syncs (Android pulls the tombstone and hides it).
    await updateRecurringRule(rule.firestoreId || rule.id, { remoteState: REMOTE_STATE_DELETE });
  };

  const markNotificationRead = async (notif) => {
    if (!currentUser || !firebaseFirestore) return;
    await updateDoc(
      doc(firebaseFirestore, `users/${currentUser.uid}/notifications`, String(notif.firestoreId || notif.id)),
      { read: true, updatedAt: Date.now() }
    );
  };

  const markAllNotificationsRead = async () => {
    if (!currentUser || !firebaseFirestore) return;
    const unread = notifications.filter((n) => !n.read);
    if (unread.length === 0) return;
    const batch = writeBatch(firebaseFirestore);
    const now = Date.now();
    unread.forEach((n) => {
      batch.update(
        doc(firebaseFirestore, `users/${currentUser.uid}/notifications`, String(n.firestoreId || n.id)),
        { read: true, updatedAt: now }
      );
    });
    await batch.commit();
  };

  const dismissNotification = async (notif) => {
    if (!currentUser || !firebaseFirestore) return;
    await updateDoc(
      doc(firebaseFirestore, `users/${currentUser.uid}/notifications`, String(notif.firestoreId || notif.id)),
      { remoteState: REMOTE_STATE_DELETE, updatedAt: Date.now() }
    );
  };

  // Materialization itself lives in src/domain/recurring/materializeRecurringTasks.js
  // (mirrors Android's domain/recurring/MaterializeRecurringTasks.kt and iOS's
  // MaterializeRecurringTasks.swift). This wrapper just feeds it the live
  // Firestore handle + context state and guards against overlapping runs.
  const materializeDueRecurringRules = useCallback(async () => {
    if (!currentUser || !firebaseFirestore) return;
    if (materializingRef.current) return;
    materializingRef.current = true;
    try {
      await materializeDueRecurringRulesUseCase({
        db: firebaseFirestore,
        uid: currentUser.uid,
        rules: recurringRules,
        labels,
        actionTemplates,
      });
    } finally {
      materializingRef.current = false;
    }
  }, [currentUser, firebaseFirestore, recurringRules, labels, actionTemplates]);

  // Auto-start the recurring + notification listeners for the whole session, so
  // the bell + materialization work on every screen (not just one).
  useEffect(() => {
    if (!currentUser || !firebaseFirestore) return;
    startCollectingRecurringRules();
    startCollectingNotifications();
    // One due-date sweep per session: inbox rows for tasks due today or
    // overdue (Android runs this from its daily worker; same dedup ids).
    sweepDueDateNotifications({ db: firebaseFirestore, uid: currentUser.uid });
    // Invoices + clients aren't needed for the first paint (only the Invoice
    // screens read them), so start their listeners AFTER the boot snapshot
    // storm settles — fewer full-app re-renders while the task grid mounts
    // and the belt-title animations spin up.
    const deferred = setTimeout(() => {
      startCollectingInvoices();
      startCollectingClients();
    }, 4000);
    return () => {
      clearTimeout(deferred);
      if (recurringRulesUnsubscribeRef.current) recurringRulesUnsubscribeRef.current();
      if (notificationsUnsubscribeRef.current) notificationsUnsubscribeRef.current();
      if (invoicesUnsubscribeRef.current) invoicesUnsubscribeRef.current();
      if (clientsUnsubscribeRef.current) clientsUnsubscribeRef.current();
    };
  }, [
    currentUser,
    firebaseFirestore,
    startCollectingRecurringRules,
    startCollectingNotifications,
    startCollectingInvoices,
    startCollectingClients,
  ]);

  // Materialize due rules on mount, whenever the rule set changes, and on a
  // 60s tick while the app is open.
  useEffect(() => {
    if (!currentUser || !firebaseFirestore) return;
    materializeDueRecurringRules();
    const interval = setInterval(() => materializeDueRecurringRules(), 60000);
    return () => clearInterval(interval);
  }, [currentUser, firebaseFirestore, materializeDueRecurringRules]);

  const unreadNotificationCount = notifications.filter((n) => !n.read).length;

  if (authLoading || dataLoading) {
    return <div>Firebase Loading...</div>;
  }

  return (
    <FirebaseContext.Provider
      value={{
        firebaseApp,
        firebaseAuth,
        firebaseAnalytics,
        firebaseFirestore,
        currentUser,
        projects,
        tasks,
        activeProject,
        allTimeRecords,
        projectTimeRecords,
        todayTimeRecords,
        todayTimeRecordsByProject,
        lastRecordedTask,
        wipTasks,
        taskDetail,
        trackedTasks,
        startCollectingTrackedTasks,
        requestStopTracking,
        dismissTrackedTask,
        getTaskTotalSpent,
        getActionLabel,
        getProjectStats,
        enforceSingleTracking,
        labels,
        startCollectingLabels,
        createLabel,
        updateLabel,
        deleteLabel,
        updateTaskLabels,
        actionTemplates,
        startCollectingActionTemplates,
        createActionTemplate,
        updateActionTemplate,
        deleteActionTemplate,
        getFreshToken,
        markTaskAsDone,
        completeTask,
        createNewTask,
        handleProjectChange,
        updateProject,
        deleteProject,
        updateTask,
        deleteTask,
        deleteTaskWithUndo,
        deleteTimeRecord,
        fetchTimeRecords,
        fetchActions,
        createAction,
        updateAction,
        deleteAction,
        convertActionToTask,
        restoreAction,
        setTaskState,
        setTaskTrackingState,
        setTaskAiTrackingState,
        finalizeActionTimers,
        createNewProject,
        startCollectingTimeRecords,
        startCollectingTodayTimeRecords,
        startCollectingTodayTimeRecordsByProject,
        updateRecordingTaskInFirestore,
        updateTimeRecord,
        startCollectingWipTasks,
        startCollectingTaskById,
        updateTaskDetail,
        // Recurring rules + notification inbox
        recurringRules,
        notifications,
        unreadNotificationCount,
        startCollectingRecurringRules,
        startCollectingNotifications,
        createRecurringRule,
        updateRecurringRule,
        toggleRecurringRule,
        deleteRecurringRule,
        markNotificationRead,
        markAllNotificationsRead,
        dismissNotification,
        materializeDueRecurringRules,
        // Invoices + clients
        invoices,
        clients,
        startCollectingInvoices,
        startCollectingClients,
        createInvoice,
        duplicateLastInvoice,
        updateInvoice,
        deleteInvoice,
        createClient,
        updateClient,
        deleteClient,
        getInvoiceSourceData,
      }}
    >
      {children}
    </FirebaseContext.Provider>
  );
};
